import React, { useEffect, useState, useCallback } from "react";
import { UserEditDialog } from "../enterprise/admin/UserEditDialog";
import { toast } from "sonner";
import {
  ujmsRoles, ujmsPermissions, ujmsCreateRole, ujmsDeleteRole, ujmsGrantPerm, ujmsRevokePerm,
  ujmsUsers, ujmsAssignRole, ujmsRemoveRole, ujmsCreateUser, ujmsSetUserStatus, ujmsResetUserPassword, ujmsUnlockUser,
  ujmsHolidays, ujmsAddHoliday, ujmsDeleteHoliday, ujmsSettings, ujmsSaveSettings, ujmsAutofillHolidays,
  ujmsSetBrandingLogo, ujmsSetLoginBg,
} from "../../services/ujmsApi";
import { getBranding, clearBrandingCache, brandingLogoUrl, loginBackground, applyBranding, faviconFor, BrandMark } from "../branding";
import { THEME_PRESETS, hsl } from "../theme";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ShieldPlus, Trash2, KeyRound, LockOpen, UserPlus, CalendarOff, Check, Palette, Pencil } from "lucide-react";

const LEGACY_ROLES = ["Court Administrator", "Clerk of Court", "Clerk Supervisor", "Judge", "Attorney",
  "State's Attorney", "Law Enforcement", "Pretrial Services Officer", "Corrections Officer", "Probation Officer"];

const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [nr, setNr] = useState({ name: "", copy_from_role_id: "" });
  const [grant, setGrant] = useState({});
  const load = useCallback(() => {
    ujmsRoles().then((r) => setRoles(r.data)).catch(() => {});
    ujmsPermissions().then((r) => setPerms(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const createRole = async () => {
    if (!nr.name.trim()) { toast.error("Role name is required"); return; }
    try {
      await ujmsCreateRole({ name: nr.name, copy_from_role_id: nr.copy_from_role_id || null });
      toast.success(`Role '${nr.name}' created`); setNr({ name: "", copy_from_role_id: "" }); load();
    } catch (e) { err(e, "Failed to create role"); }
  };
  const removeRole = async (r) => {
    try { await ujmsDeleteRole(r.id); toast.success(`Role '${r.name}' deleted`); load(); }
    catch (e) { err(e, "Failed to delete role"); }
  };
  const doGrant = async (r) => {
    const code = grant[r.id];
    if (!code) return;
    try { await ujmsGrantPerm(r.id, code); toast.success(`${code} granted to ${r.name}`); setGrant({ ...grant, [r.id]: "" }); load(); }
    catch (e) { err(e, "Failed to grant"); }
  };
  const doRevoke = async (r, code) => {
    try { await ujmsRevokePerm(r.id, code); toast.success(`${code} revoked from ${r.name}`); load(); }
    catch (e) { err(e, "Failed to revoke"); }
  };

  return (
    <div className="space-y-4">
      <Panel title="Create a custom role" testId="create-role-panel">
        <div className="p-3 flex flex-wrap items-end gap-2">
          <Field label="Role name"><input className={`${inputCls} w-56`} value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} placeholder="e.g. Records Auditor" data-testid="new-role-name" /></Field>
          <Field label="Copy permissions from">
            <select className={`${selectCls} w-56`} value={nr.copy_from_role_id} onChange={(e) => setNr({ ...nr, copy_from_role_id: e.target.value })} data-testid="new-role-copyfrom">
              <option value="">Start empty…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <button className={btnPrimary} onClick={createRole} data-testid="new-role-create-btn"><ShieldPlus className="w-3.5 h-3.5" /> Create Role</button>
        </div>
      </Panel>
      <Panel title="Role permission matrix — grant and revoke individual permissions" testId="roles-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Role</th><th className={thCls}>Permissions</th><th className={thCls}>Grant</th><th className={thCls}></th></tr></thead>
          <tbody>
            {roles.map((r) => {
              const avail = perms.filter((p) => !r.permissions.includes(p.code));
              return (
                <tr key={r.id} className="hover:bg-slate-50 align-top" data-testid={`role-row-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <td className={`${tdCls} font-medium whitespace-nowrap`}>
                    {r.name}
                    <div className="text-[10px] uppercase text-slate-400">{r.system_defined ? "system" : "custom"}</div>
                  </td>
                  <td className={tdCls}>
                    <div className="flex flex-wrap gap-1 max-w-2xl">
                      {r.permissions.map((p) => (
                        <span key={p} className="inline-flex items-center gap-1 font-mono text-[10px] border border-slate-200 bg-slate-50 rounded-sm px-1.5 py-0.5">
                          {p}
                          <button className="text-red-500 font-bold hover:text-red-700" onClick={() => doRevoke(r, p)}
                            data-testid={`revoke-${r.name.replace(/\s+/g, "-").toLowerCase()}-${p}`}>×</button>
                        </span>
                      ))}
                      {!r.permissions.length && <span className="text-xs text-slate-400">no permissions</span>}
                    </div>
                  </td>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    <div className="flex gap-1">
                      <select className={`${selectCls} w-48`} value={grant[r.id] || ""} onChange={(e) => setGrant({ ...grant, [r.id]: e.target.value })}
                        data-testid={`grant-select-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        <option value="">Add permission…</option>
                        {avail.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
                      </select>
                      <button className={btnGhost} onClick={() => doGrant(r)} data-testid={`grant-btn-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>Grant</button>
                    </div>
                  </td>
                  <td className={tdCls}>
                    {!r.system_defined && (
                      <button className="text-red-600 hover:text-red-800" title="Delete custom role" onClick={() => removeRole(r)}
                        data-testid={`delete-role-${r.name.replace(/\s+/g, "-").toLowerCase()}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sel, setSel] = useState({});
  const [nu, setNu] = useState({ username: "", name: "", password: "", legacy_role: "Clerk of Court", role_id: "" });
  const [pwTarget, setPwTarget] = useState(null);
  const [editId, setEditId] = useState(null);
  const [newPw, setNewPw] = useState("");
  const load = useCallback(() => {
    ujmsUsers().then((r) => setUsers(r.data)).catch(() => {});
    ujmsRoles().then((r) => setRoles(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (u) => {
    if (!sel[u.id]) return;
    try { await ujmsAssignRole(u.id, sel[u.id]); toast.success("USER_ROLE_ASSIGNED"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const remove = async (u, roleId) => {
    try { await ujmsRemoveRole(u.id, roleId); toast.success("Role revoked"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const createUser = async () => {
    if (!nu.username || !nu.password || !nu.name) { toast.error("Username, name and password are required"); return; }
    try {
      await ujmsCreateUser({ username: nu.username, name: nu.name, password: nu.password,
        legacy_role: nu.legacy_role, role_ids: nu.role_id ? [nu.role_id] : [] });
      toast.success(`Account '${nu.username}' created`);
      setNu({ username: "", name: "", password: "", legacy_role: "Clerk of Court", role_id: "" }); load();
    } catch (e) { err(e, "Failed to create user"); }
  };
  const toggleActive = async (u) => {
    try { await ujmsSetUserStatus(u.id, u.active === false); toast.success(u.active === false ? "Account reactivated" : "Account deactivated"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const resetPw = async () => {
    try { await ujmsResetUserPassword(pwTarget.id, newPw); toast.success(`Password reset for ${pwTarget.username}`); setPwTarget(null); setNewPw(""); }
    catch (e) { err(e, "Failed to reset password"); }
  };

  return (
    <div className="space-y-4">
      <Panel title="Provision a new account" testId="create-user-panel">
        <div className="p-3 flex flex-wrap items-end gap-2">
          <Field label="Username"><input className={`${inputCls} w-36`} value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} data-testid="new-user-username" /></Field>
          <Field label="Full name"><input className={`${inputCls} w-48`} value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="new-user-name" /></Field>
          <Field label="Password"><input type="password" className={`${inputCls} w-36`} value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} data-testid="new-user-password" /></Field>
          <Field label="Legacy role">
            <select className={`${selectCls} w-48`} value={nu.legacy_role} onChange={(e) => setNu({ ...nu, legacy_role: e.target.value })} data-testid="new-user-legacy-role">
              {LEGACY_ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="CJMS role (optional)">
            <select className={`${selectCls} w-48`} value={nu.role_id} onChange={(e) => setNu({ ...nu, role_id: e.target.value })} data-testid="new-user-ujms-role">
              <option value="">Inherit from legacy…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <button className={btnPrimary} onClick={createUser} data-testid="new-user-create-btn"><UserPlus className="w-3.5 h-3.5" /> Create Account</button>
        </div>
      </Panel>
      <Panel title="Platform users → CJMS role assignments" testId="users-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>User</th><th className={thCls}>Status</th><th className={thCls}>Assigned CJMS Roles</th><th className={thCls}>Default (legacy mapping)</th><th className={thCls}>Grant Role</th><th className={thCls}>Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={cls("hover:bg-slate-50", u.active === false && "opacity-50")} data-testid={`admin-user-${u.username}`}>
                <td className={tdCls}>{u.name}<div className="text-xs text-slate-400">{u.username} · {u.role}</div></td>
                <td className={tdCls}><Badge value={u.active === false ? "INACTIVE" : "ACTIVE"} testId={`user-status-${u.username}`} /></td>
                <td className={tdCls}>
                  {(u.assigned_roles || []).map((r) => (
                    <span key={r.role_id} className="inline-flex items-center gap-1 mr-1 mb-1 text-xs border border-blue-200 bg-blue-50 rounded-sm px-1.5 py-0.5">
                      {r.role}
                      <button className="text-red-600 font-bold" onClick={() => remove(u, r.role_id)} data-testid={`remove-role-${u.username}-${r.role_id}`}>×</button>
                    </span>
                  ))}
                  {!u.assigned_roles?.length && <span className="text-xs text-slate-400">inherits default</span>}
                </td>
                <td className={`${tdCls} text-xs`}>{(u.default_roles || []).join(", ") || "—"}</td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  <div className="flex gap-1">
                    <select className={`${selectCls} w-44`} value={sel[u.id] || ""} onChange={(e) => setSel({ ...sel, [u.id]: e.target.value })} data-testid={`role-select-${u.username}`}>
                      <option value="">Select role…</option>
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <button className={btnGhost} onClick={() => assign(u)} data-testid={`role-assign-${u.username}`}>Grant</button>
                  </div>
                </td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  <div className="flex gap-1.5">
                    <button className={btnGhost} onClick={() => setEditId(u.id)} title="Edit account (profile, contact, agency, courtroom, seat)" data-testid={`edit-user-${u.username}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button className={btnGhost} onClick={() => { setPwTarget(u); setNewPw(""); }} title="Reset password" data-testid={`reset-pw-${u.username}`}><KeyRound className="w-3.5 h-3.5" /></button>
                    <button className={btnGhost} onClick={async () => { try { const r = await ujmsUnlockUser(u.username); toast.success(`${u.username} unlocked — ${r.data.cleared} lockout record(s) cleared`); } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); } }} title="Unlock sign-in (clear failed-attempt lockout)" data-testid={`unlock-${u.username}`}><LockOpen className="w-3.5 h-3.5" /></button>
                    <button className={cls("text-xs font-medium px-2 py-1 rounded-sm border",
                      u.active === false ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-red-300 text-red-700 hover:bg-red-50")}
                      onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.username}`}>
                      {u.active === false ? "Reactivate" : "Deactivate"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <UserEditDialog userId={editId} onClose={() => setEditId(null)} onSaved={load} />
      <Dialog open={!!pwTarget} onOpenChange={(v) => !v && setPwTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password — {pwTarget?.username}</DialogTitle></DialogHeader>
          <Field label="New password (min 6 characters)" required>
            <input type="password" className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-pw-input" />
          </Field>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setPwTarget(null)}>Cancel</button>
            <button className={btnPrimary} disabled={newPw.length < 6} onClick={resetPw} data-testid="reset-pw-submit">Reset Password</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function HolidaysTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date: "", label: "", closure_type: "FULL" });
  const [year, setYear] = useState(new Date().getFullYear());
  const load = useCallback(() => { ujmsHolidays().then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await ujmsAddHoliday(f); toast.success(`Closure added — ${f.date}`); setF({ date: "", label: "", closure_type: "FULL" }); load(); }
    catch (e) { err(e, "Failed to add closure"); }
  };
  const del = async (h) => {
    try { await ujmsDeleteHoliday(h.id); toast.success("Closure removed"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const autofill = async () => {
    try {
      const r = await ujmsAutofillHolidays(Number(year));
      toast.success(`${r.data.added} federal holiday(s) loaded for ${year}${r.data.skipped ? ` — ${r.data.skipped} already present` : ""}`);
      load();
    } catch (e) { err(e, "Autofill failed"); }
  };

  return (
    <div className="space-y-4">
      <Panel title="Add a court holiday / closure" testId="add-holiday-panel">
        <div className="p-3 flex flex-wrap items-end gap-2">
          <Field label="Date" required><input type="date" className={`${inputCls} w-40`} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="holiday-date" /></Field>
          <Field label="Label" required><input className={`${inputCls} w-64`} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="e.g. Independence Day" data-testid="holiday-label" /></Field>
          <Field label="Closure">
            <select className={`${selectCls} w-32`} value={f.closure_type} onChange={(e) => setF({ ...f, closure_type: e.target.value })} data-testid="holiday-type">
              <option value="FULL">FULL</option>
              <option value="PARTIAL">PARTIAL</option>
            </select>
          </Field>
          <button className={btnPrimary} disabled={!f.date || !f.label} onClick={add} data-testid="holiday-add-btn"><CalendarOff className="w-3.5 h-3.5" /> Add Closure</button>
          <div className="flex items-end gap-1 border-l border-slate-200 pl-3 ml-1">
            <Field label="Year"><input type="number" className={`${inputCls} w-24`} value={year} onChange={(e) => setYear(e.target.value)} data-testid="holiday-autofill-year" /></Field>
            <button className={btnGhost} onClick={autofill} data-testid="holiday-autofill-btn">Load Federal Holidays</button>
          </div>
        </div>
        <p className="px-3 pb-3 text-xs text-slate-400">Scheduling a hearing on a FULL closure date raises a COURT_CLOSURE conflict that requires an override reason.</p>
      </Panel>
      <Panel title="Court calendar — holidays and closures" testId="holidays-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Date</th><th className={thCls}>Label</th><th className={thCls}>Closure</th><th className={thCls}>Added By</th><th className={thCls}></th></tr></thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50" data-testid={`holiday-row-${h.date}`}>
                <td className={`${tdCls} font-mono text-xs`}>{h.date}</td>
                <td className={tdCls}>{h.label}</td>
                <td className={tdCls}><Badge value={h.closure_type} /></td>
                <td className={`${tdCls} text-xs`}>{h.created_by || "—"}</td>
                <td className={tdCls}><button className="text-red-600 hover:text-red-800" onClick={() => del(h)} data-testid={`holiday-delete-${h.date}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td className={`${tdCls} text-slate-400`} colSpan={5}>No closures configured.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

const COURT_PRESETS = [
  ["MUNICIPAL", "Municipal Court", "Municipal Court"],
  ["COUNTY", "County Court", "County Court"],
  ["SUPERIOR", "Superior Court", "Superior Court"],
  ["CIRCUIT", "Circuit Court", "Circuit Court"],
];

export function SettingsTab() {
  const [s, setS] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [brandInfo, setBrandInfo] = useState(null);
  useEffect(() => {
    ujmsSettings().then((r) => setS(r.data)).catch(() => {});
    getBranding(true).then(setBrandInfo);
  }, []);
  const save = async () => {
    try {
      const { court_name, county_name, court_type, court_tagline, clerk_of_court_name,
        session_start, session_end, default_hearing_duration, timezone_label, public_portal_enabled,
        theme_preset, theme_font, login_bg_mode, login_bg_color, seal_style, seal_overlay, favicon_mode } = s;
      await ujmsSaveSettings({ court_name, county_name: county_name || "", court_type, court_tagline, clerk_of_court_name,
        session_start, session_end,
        default_hearing_duration: Number(default_hearing_duration), timezone_label, public_portal_enabled,
        theme_preset: theme_preset || "indigo", theme_font: theme_font || "Outfit",
        login_bg_mode: login_bg_mode || "image", login_bg_color: login_bg_color || "#312e81",
        seal_style: seal_style || "light", seal_overlay: seal_overlay || "none", favicon_mode: favicon_mode || "palette" });
      clearBrandingCache();
      setBrandInfo(await getBranding(true));
      toast.success("System settings saved and audited — theme and branding now apply everywhere");
    } catch (e) { err(e, "Failed to save settings"); }
  };
  const previewTheme = (patch) => {
    setS((prev) => {
      const next = { ...prev, ...patch };
      applyBranding({ ...(brandInfo || {}), ...next });
      return next;
    });
  };
  const uploadLoginBg = (file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Background image must be under 1.5MB"); return; }
    setLogoBusy(true);
    const rd = new FileReader();
    rd.onload = async () => {
      try {
        await ujmsSetLoginBg({ logo_b64: String(rd.result).split(",")[1], logo_mime: file.type || "image/jpeg" });
        clearBrandingCache();
        setBrandInfo(await getBranding(true));
        setS((prev) => ({ ...prev, login_bg_mode: "custom" }));
        toast.success("Login background uploaded and applied");
      } catch (e) { err(e, "Background upload failed"); }
      setLogoBusy(false);
    };
    rd.readAsDataURL(file);
  };
  const clearLoginBg = async () => {
    try {
      await ujmsSetLoginBg({ clear: true });
      clearBrandingCache();
      setBrandInfo(await getBranding(true));
      setS((prev) => ({ ...prev, login_bg_mode: "image" }));
      toast.success("Custom login background removed — built-in courthouse image restored");
    } catch (e) { err(e, "Failed to remove background"); }
  };
  const pickPreset = (code) => {
    const p = COURT_PRESETS.find((x) => x[0] === code);
    setS((prev) => ({ ...prev, court_type: code, court_name: p ? p[2] : prev.court_name }));
  };
  const uploadLogo = (file) => {
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Logo must be under 512KB"); return; }
    setLogoBusy(true);
    const rd = new FileReader();
    rd.onload = async () => {
      try {
        await ujmsSetBrandingLogo({ logo_b64: String(rd.result).split(",")[1], logo_mime: file.type || "image/png" });
        clearBrandingCache();
        setBrandInfo(await getBranding(true));
        toast.success("Logo uploaded — it now replaces the built-in mark across the platform");
      } catch (e) { err(e, "Logo upload failed"); }
      setLogoBusy(false);
    };
    rd.readAsDataURL(file);
  };
  const clearLogo = async () => {
    try {
      await ujmsSetBrandingLogo({ clear: true });
      clearBrandingCache();
      setBrandInfo(await getBranding(true));
      toast.success("Custom logo removed — reverted to the built-in neutral mark");
    } catch (e) { err(e, "Failed to remove logo"); }
  };
  if (!s) return <div className="text-sm text-slate-400 p-4">Loading settings…</div>;
  return (
    <div className="space-y-4">
    <Panel title="Court identity & branding — shown on login, headers, the public portal and generated orders" testId="branding-panel">
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        <Field label="Court type (preset)">
          <select className={selectCls} value={s.court_type || "COUNTY"} onChange={(e) => pickPreset(e.target.value)} data-testid="setting-court-type">
            {COURT_PRESETS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </Field>
        <Field label="Court name"><input className={inputCls} value={s.court_name || ""} onChange={(e) => setS({ ...s, court_name: e.target.value })} placeholder="e.g. Superior Court of Riverton County" data-testid="setting-court-name" /></Field>
        <Field label="County (printed on charging documents, e.g. “State's Attorney of ___ County”; leave blank to omit)"><input className={inputCls} value={s.county_name || ""} onChange={(e) => setS({ ...s, county_name: e.target.value })} placeholder="e.g. Riverton" data-testid="setting-county-name" /></Field>
        <Field label="Tagline"><input className={inputCls} value={s.court_tagline || ""} onChange={(e) => setS({ ...s, court_tagline: e.target.value })} data-testid="setting-court-tagline" /></Field>
        <Field label="Clerk of the Court (appears on order stamps & certifications)"><input className={inputCls} value={s.clerk_of_court_name || ""} onChange={(e) => setS({ ...s, clerk_of_court_name: e.target.value })} data-testid="setting-clerk-name" /></Field>
        <Field label="Court logo (PNG/JPG, max 512KB — replaces the built-in mark)">
          <div className="flex items-center gap-3">
            {brandInfo?.has_logo
              ? <img src={brandingLogoUrl()} alt="Current court logo" className="w-10 h-10 object-contain border border-slate-200 rounded-sm bg-white" data-testid="setting-logo-preview" />
              : <span className="text-xs text-slate-400" data-testid="setting-logo-none">Built-in mark in use</span>}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="text-xs" disabled={logoBusy}
              onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="setting-logo-upload" />
            {brandInfo?.has_logo && <button className="text-xs text-red-700 underline" onClick={clearLogo} data-testid="setting-logo-clear">Remove</button>}
          </div>
        </Field>
      </div>
    </Panel>
    <AppearancePanel s={s} brandInfo={brandInfo} previewTheme={previewTheme} uploadLoginBg={uploadLoginBg} clearLoginBg={clearLoginBg} busy={logoBusy} />
    <Panel title="System configuration — changes are recorded in the audit log" testId="settings-panel">
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        <Field label="Timezone"><input className={inputCls} value={s.timezone_label || ""} onChange={(e) => setS({ ...s, timezone_label: e.target.value })} data-testid="setting-timezone" /></Field>
        <Field label="Court session opens"><input type="time" className={inputCls} value={s.session_start || ""} onChange={(e) => setS({ ...s, session_start: e.target.value })} data-testid="setting-session-start" /></Field>
        <Field label="Court session closes"><input type="time" className={inputCls} value={s.session_end || ""} onChange={(e) => setS({ ...s, session_end: e.target.value })} data-testid="setting-session-end" /></Field>
        <Field label="Default hearing duration (minutes)"><input type="number" className={inputCls} value={s.default_hearing_duration ?? 30} onChange={(e) => setS({ ...s, default_hearing_duration: e.target.value })} data-testid="setting-duration" /></Field>
        <Field label="Public case portal">
          <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
            <input type="checkbox" checked={!!s.public_portal_enabled} onChange={(e) => setS({ ...s, public_portal_enabled: e.target.checked })} data-testid="setting-portal-enabled" />
            Enabled
          </label>
        </Field>
      </div>
      <div className="px-4 pb-4">
        <button className={btnPrimary} onClick={save} data-testid="settings-save-btn">Save Settings</button>
      </div>
    </Panel>
    </div>
  );
}

const SEAL_STYLES = [["light", "Light disc"], ["solid", "Solid palette"], ["outline", "Outlined"]];
const SEAL_OVERLAYS = [["none", "Original logo"], ["tint", "Palette tint"]];
const BG_MODES = [
  ["image", "Built-in courthouse photo"],
  ["custom", "Custom uploaded image"],
  ["color", "Solid colour"],
  ["gradient", "Theme gradient"],
];

function AppearancePanel({ s, brandInfo, previewTheme, uploadLoginBg, clearLoginBg, busy }) {
  const preset = s.theme_preset || "indigo";
  const previewBrand = { ...(brandInfo || {}), theme_preset: preset, login_bg_mode: s.login_bg_mode || "image", login_bg_color: s.login_bg_color || "#312e81",
    seal_style: s.seal_style || "light", seal_overlay: s.seal_overlay || "none", favicon_mode: s.favicon_mode || "palette", court_name: s.court_name };
  return (
    <Panel title="Appearance & theme — colour palette, typeface and login screen background" testId="appearance-panel">
      <div className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-5">
          <Field label="Colour palette (applies to menus, headers, tabs, buttons and links system-wide)">
            <div className="flex flex-wrap gap-2 mt-1" data-testid="theme-preset-swatches">
              {Object.entries(THEME_PRESETS).map(([key, p]) => (
                <button key={key} type="button" onClick={() => previewTheme({ theme_preset: key })} title={p.label}
                  className={cls("flex items-center gap-2 border rounded-sm pl-1.5 pr-3 py-1.5 text-xs font-semibold transition-colors",
                    preset === key ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400")}
                  data-testid={`theme-preset-${key}`} aria-pressed={preset === key}>
                  <span className="w-6 h-6 rounded-full grid place-items-center text-white" style={{ background: `linear-gradient(135deg, ${hsl(p.h, p.s, p.l + 6)}, ${hsl(p.h, p.s, p.l - 20)})` }}>
                    {preset === key && <Check className="w-3.5 h-3.5" />}
                  </span>
                  {p.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Typeface">
            <div className="text-[12px] text-slate-700 mt-1 border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid="theme-font-fixed">
              <span className="font-heading font-bold text-[15px]">Times New Roman</span> for headings &amp; case captions · <span className="font-sans">Arial</span> for body and interface text — court standard, not configurable.
            </div>
          </Field>
          <Field label="Court seal & favicon (follow the palette)">
            <div className="flex flex-wrap items-center gap-4 mt-1" data-testid="seal-options">
              {!brandInfo?.has_logo ? SEAL_STYLES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => previewTheme({ seal_style: v })}
                  className={cls("flex items-center gap-2 border rounded-sm pl-1.5 pr-3 py-1.5 text-xs font-semibold transition-colors",
                    (s.seal_style || "light") === v ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400")}
                  data-testid={`seal-style-${v}`} aria-pressed={(s.seal_style || "light") === v}>
                  <span className="ody2-titlebar rounded-sm p-1 inline-flex"><BrandMark className="w-7 h-7" brand={{ ...previewBrand, seal_style: v }} /></span>
                  {l}
                </button>
              )) : SEAL_OVERLAYS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => previewTheme({ seal_overlay: v })}
                  className={cls("flex items-center gap-2 border rounded-sm pl-1.5 pr-3 py-1.5 text-xs font-semibold transition-colors",
                    (s.seal_overlay || "none") === v ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400")}
                  data-testid={`seal-overlay-${v}`} aria-pressed={(s.seal_overlay || "none") === v}>
                  <span className="bg-slate-100 rounded-sm p-1 inline-flex"><BrandMark className="w-7 h-7" brand={{ ...previewBrand, seal_overlay: v }} /></span>
                  {l}
                </button>
              ))}
              <label className="flex items-center gap-2 text-xs border-l border-slate-200 pl-4">
                <span className="text-slate-500 font-semibold uppercase tracking-wide text-[10px]">Favicon</span>
                <select className={`${selectCls} w-44`} value={s.favicon_mode || "palette"} onChange={(e) => previewTheme({ favicon_mode: e.target.value })} data-testid="favicon-mode">
                  <option value="palette">Palette seal (generated)</option>
                  <option value="logo" disabled={!brandInfo?.has_logo}>{brandInfo?.has_logo ? "Uploaded court logo" : "Uploaded court logo (upload first)"}</option>
                </select>
                <img src={faviconFor(previewBrand)} alt="Favicon preview" className="w-6 h-6 border border-slate-200 rounded-sm bg-white" data-testid="favicon-preview" />
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-1">The browser-tab icon is regenerated from the palette; with an uploaded logo you can tint it to the palette colour instead.</p>
          </Field>
          <Field label="Login screen background">
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <select className={`${selectCls} w-60`} value={s.login_bg_mode || "image"} onChange={(e) => previewTheme({ login_bg_mode: e.target.value })} data-testid="login-bg-mode">
                {BG_MODES.map(([v, l]) => <option key={v} value={v} disabled={v === "custom" && !brandInfo?.has_login_bg}>{v === "custom" && !brandInfo?.has_login_bg ? `${l} (upload first)` : l}</option>)}
              </select>
              {(s.login_bg_mode === "color") && (
                <label className="flex items-center gap-2 text-xs">
                  <input type="color" value={s.login_bg_color || "#312e81"} onChange={(e) => previewTheme({ login_bg_color: e.target.value })} data-testid="login-bg-color" />
                  <span className="font-mono">{s.login_bg_color}</span>
                </label>
              )}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="text-xs" disabled={busy}
                onChange={(e) => uploadLoginBg(e.target.files?.[0])} data-testid="login-bg-upload" />
              {brandInfo?.has_login_bg && <button type="button" className="text-xs text-red-700 underline" onClick={clearLoginBg} data-testid="login-bg-clear">Remove custom image</button>}
            </div>
            <p className="text-xs text-slate-400 mt-1">JPG/PNG/WebP up to 1.5MB. Changes preview instantly here; press Save Settings below to apply for everyone.</p>
          </Field>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5 flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> Login preview</div>
          <div className="relative h-[190px] rounded-sm border border-slate-200 overflow-hidden grid place-items-center" style={{ background: loginBackground(previewBrand) }} data-testid="login-bg-preview">
            <div className="bg-white shadow-xl w-[150px] px-3 py-3 text-center">
              <div className="text-[9px] font-bold text-ujms-deep truncate">{s.court_name}</div>
              <div className="mt-2 h-4 border border-slate-300" />
              <div className="mt-2 h-5 bg-ujms text-white text-[8px] font-semibold grid place-items-center">Sign In</div>
            </div>
          </div>
          <div className="mt-3 rounded-sm overflow-hidden border border-slate-200" data-testid="theme-chrome-preview">
            <div className="ody2-titlebar text-white text-[10px] px-2 py-1 font-semibold">CJMS — {s.court_name}</div>
            <div className="ody2-menurow text-[10px] px-2 py-0.5 text-slate-800">File &nbsp; View &nbsp; Reports &nbsp; Tools</div>
            <div className="p-2 flex gap-1"><span className="ody2-tab text-[9px] px-2 py-0.5">Summary</span><span className="ody2-tab ody2-tab-active text-[9px] px-2 py-0.5">Hearings</span></div>
            <div className="tyler-th text-[9px] px-2 py-0.5">Case Number</div>
          </div>
        </div>
      </div>
    </Panel>
  );
}
